import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInvalidateDecedentQueries } from '@/entities/decedent';
import {
  createTaskStepNote,
  deleteTaskStepNote,
  getTask,
  updateTaskStatus,
  updateTaskStep,
} from '@/entities/task/task.api';
import { taskQueryKeys } from '@/entities/task/task.keys';

export const useTaskQuery = (taskId, options = {}) =>
  useQuery({
    queryKey: taskQueryKeys.detail(taskId),
    queryFn: () => getTask(taskId),
    enabled: !!taskId,
    ...options,
  });

export const useUpdateTaskStatusMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { invalidateAllTaskLists, invalidateDecedent } =
    useInvalidateDecedentQueries();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, status }) => updateTaskStatus(taskId, status),
    onSuccess: async (data, variables, context) => {
      const { taskId, decedentId } = variables;

      await queryClient.invalidateQueries({
        queryKey: taskQueryKeys.detail(taskId),
      });

      if (decedentId) {
        await invalidateAllTaskLists(decedentId);
        await invalidateDecedent(decedentId);
      }

      await onSuccess?.(data, variables, context);
    },
    ...mutationOptions,
  });
};

export const useUpdateTaskStepMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { invalidateAllTaskLists, invalidateDecedent } =
    useInvalidateDecedentQueries();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: ({ stepId, isDone }) => updateTaskStep(stepId, isDone),
    onSuccess: async (data, variables, context) => {
      const { taskId, decedentId } = variables;

      if (taskId) {
        await queryClient.invalidateQueries({
          queryKey: taskQueryKeys.detail(taskId),
        });
      }

      if (decedentId) {
        await invalidateAllTaskLists(decedentId);
        await invalidateDecedent(decedentId);
      }

      await onSuccess?.(data, variables, context);
    },
    ...mutationOptions,
  });
};

export const useCreateTaskStepNoteMutation = (options = {}) =>
  useMutation({
    mutationFn: ({ stepId, note }) => createTaskStepNote(stepId, note),
    ...options,
  });

export const useDeleteTaskStepNoteMutation = (options = {}) =>
  useMutation({
    mutationFn: (noteId) => deleteTaskStepNote(noteId),
    ...options,
  });
